import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { EquationDefinition, ImpactPreviewOutput, ImpactRow } from 'used-vehicle-contracts';
import { orpc } from '../../../apis/orpc-query';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../../components/ui/DataTable';
import { InfoCell } from '../../../components/ui/InfoCell';
import { formatDecimal, formatMoney } from '../../../lib/format';
import { serverMessage, submitErrorKey } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';

type Translate = (key: string) => string;

/** A figure that could not be computed prints an em dash; the two sides are shown as `before → after`. */
const pair = (t: Translate, before: number | null, after: number | null, money: boolean) => (
  <span
    dir="ltr"
    className="inline-block tabular-nums text-foreground"
  >
    {before === null ? t('common.notSet') : money ? formatMoney(before) : formatDecimal(before)} →{' '}
    {after === null ? t('common.notSet') : money ? formatMoney(after) : formatDecimal(after)}
  </span>
);

/** A delta of nothing is left blank; a delta of **zero** is printed, because "unchanged" is a finding. */
const delta = (t: Translate, value: number | null, money: boolean) => {
  if (value === null) {
    return <span className="text-muted-foreground">{t('common.notSet')}</span>;
  }

  const formatted = money ? formatMoney(Math.abs(value)) : formatDecimal(Math.abs(value));
  const sign = value > 0 ? '+' : value < 0 ? '−' : '±';

  return (
    <span
      dir="ltr"
      className="inline-block tabular-nums text-foreground"
    >
      {sign}
      {formatted}
    </span>
  );
};

export interface ImpactPreviewCardProps {
  equations: readonly EquationDefinition[];
  /** Blocked while the local compile is unhappy: pricing with a broken policy tells nobody anything. */
  disabled: boolean;
  /** The whole answer goes up as well: the publish dialog states the incomplete count (plan §11 fix 15). */
  onResult: (result: ImpactPreviewOutput) => void;
}

/**
 * The impact preview (PP AC08).
 *
 * "What would these equations do to the estimates that are currently open?" — priced with each
 * estimate's **own** template version and inspection, so the before/after comparison is like for like.
 * It is the evidence an administrator needs before choosing the `OPEN_ESTIMATES` publish scope, which
 * is the only action in the workspace that moves numbers on records nobody has touched.
 */
export const ImpactPreviewCard = ({ equations, disabled, onResult }: ImpactPreviewCardProps) => {
  const { t } = useI18nTranslation();
  const [rows, setRows] = useState<ImpactRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const impactPreview = useMutation(orpc.policies.impactPreview.mutationOptions());

  const onRun = async (): Promise<void> => {
    setError(null);

    try {
      const result = await impactPreview.mutateAsync({ equations: [...equations] });

      setRows(result.rows);
      onResult(result);
    } catch (failure: unknown) {
      setError(serverMessage(failure) ?? t(submitErrorKey(failure)));
    }
  };

  const columns = useMemo<DataTableColumns<ImpactRow>>(
    () => [
      {
        id: 'estimate',
        header: t('estimates.columnEstimate'),
        accessorFn: (row) => row.estimateNo,
        cell: ({ row }) => (
          <div className="min-w-0 space-y-1">
            <InfoCell
              title={row.original.customerName ?? row.original.vehicle}
              subline={row.original.estimateNo}
            />
            {row.original.incomplete ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge tone="warning">{t('equations.impactIncomplete')}</Badge>
                {/* Why this row could not be priced, so the administrator reads a cause rather than a gap. */}
                {row.original.reason === null ? null : (
                  <span className="text-xs text-muted-foreground">{t(`equations.impactReasons.${row.original.reason}`)}</span>
                )}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'age',
        header: t('equations.impactAge'),
        enableSorting: false,
        cell: ({ row }) => pair(t, row.original.before.age, row.original.after.age, false),
      },
      {
        id: 'appraisal',
        header: t('equations.impactAppraisal'),
        enableSorting: false,
        cell: ({ row }) => pair(t, row.original.before.appraisal, row.original.after.appraisal, true),
      },
      {
        id: 'ceiling',
        header: t('equations.impactCeiling'),
        enableSorting: false,
        cell: ({ row }) => pair(t, row.original.before.ceiling, row.original.after.ceiling, true),
      },
      {
        id: 'insurance',
        header: t('equations.impactInsurance'),
        enableSorting: false,
        cell: ({ row }) => pair(t, row.original.before.insurance, row.original.after.insurance, true),
      },
      {
        id: 'delta',
        header: t('equations.impactDelta'),
        enableSorting: false,
        cell: ({ row }) => delta(t, row.original.delta.appraisal, true),
      },
    ],
    [t],
  );

  return (
    <section className="space-y-4 rounded-lg border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('equations.impactTitle')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('equations.impactSubtitle')}</p>
        </div>
        <Button
          variant="outline"
          disabled={disabled}
          isLoading={impactPreview.isPending}
          onClick={() => void onRun()}
        >
          {t('equations.impactRun')}
        </Button>
      </div>

      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}

      {rows === null ? (
        <p className="text-sm text-muted-foreground">{t('equations.impactHint')}</p>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          emptyMessage={t('equations.impactEmpty')}
          pageSize={10}
        />
      )}
    </section>
  );
};
